import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Stack, Theme, useMediaQuery } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import { HXCIcon } from '@ui-kit/components/HXCIcon';
import { Text } from '@ui-kit/components/Text';
import {
  CarouselDotPagination,
  useCarouselDotPagination,
} from '@ui-kit/components/CarouselDotPagination';
import type { SxProps } from '@mui/material/styles';

export type Testimonial = {
  quote: string;
  name: string;
  company: string;
};

export type TestimonialCarouselProps = {
  title: string;
  testimonials: ReadonlyArray<Testimonial>;
  sx?: SxProps<Theme>;
  className?: string;
};

const DESKTOP_SLIDES_PER_VIEW = 3;
const MOBILE_SLIDES_PER_VIEW = 1;
const SLIDE_GAP_PX = 32;

const addSmartQuotesFull = (text: string): string => {
  const trimmed = text
    .trim()
    .replace(/^["""']+/, '')
    .replace(/["""']+$/, '');
  return `"${trimmed}"`;
};

const TestimonialCard = React.forwardRef<HTMLDivElement, Testimonial>(
  ({ quote, name, company }, ref) => {
    const quoteRef = React.useRef<HTMLParagraphElement | null>(null);
    const fittedText = addSmartQuotesFull(quote);
    return (
      <Box
        ref={ref}
        aria-label={`Testimonial from ${name}`}
        sx={{
          position: 'relative',
          border: `1px solid black`,
          borderRadius: '0.5rem',
          backgroundColor: (theme) => theme.palette.common.white,
          py: { xs: '1.25rem', md: '1.5rem' },
          pb: { xs: '2rem', md: '2rem' },
          pl: { xs: '1.25rem', md: '1.5rem' },
          pr: { xs: '0.25rem', md: '1.1875rem' },
          height: '13rem',
          display: 'flex',
          flexDirection: 'column',
          '&:before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            borderStyle: 'solid',
            borderWidth: '14px 14px 0 14px',
            borderColor: 'black transparent transparent',
            transform: 'translate(-50%, 100%)',
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            borderStyle: 'solid',
            borderWidth: '13px 13px 0 13px',
            borderColor: (theme) => `${theme.palette.common.white} transparent transparent`,
            transform: 'translate(-50%, 100%)',
          },
        }}
      >
        <Text
          component="p"
          variant="p"
          marginBottom="0.75rem"
          ref={quoteRef}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            position: 'relative',
            pr: '0.75rem',
          }}
        >
          {fittedText}
        </Text>
        <Text
          component="p"
          variant="p"
          fontWeight="bold"
          mb="0.25rem"
          sx={{ lineHeight: { xs: 1, sm: 1 } }}
        >
          {name}
        </Text>
        <Text component="p" variant="p2" color="darkGray" sx={{ lineHeight: { xs: 1, sm: 1 } }}>
          {company}
        </Text>
      </Box>
    );
  },
);

const TestimonialCarousel = ({ title, testimonials, sx, className }: TestimonialCarouselProps) => {
  const isDesktop = useMediaQuery((t: Theme) => t.breakpoints.up('sm'));

  const slidesPerView = isDesktop ? DESKTOP_SLIDES_PER_VIEW : MOBILE_SLIDES_PER_VIEW;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    slidesToScroll: slidesPerView,
  });

  const { selectedIndex, scrollSnaps, onDotClick } = useCarouselDotPagination(emblaApi);

  const handleNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const handlePrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);

  const truncated = useMemo(
    () => testimonials.map((t) => ({ ...t, quote: addSmartQuotesFull(t.quote) })),
    [testimonials],
  );

  const slideWidthPercent = `calc(${100 / slidesPerView}% - ${SLIDE_GAP_PX * ((slidesPerView - 1) / slidesPerView)}px)`;

  return (
    <Box className={className} sx={sx}>
      <Stack spacing="2.5rem">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Text component="h2" variant="h2">
            {title}
          </Text>
          {isDesktop && (
            <Stack direction="row" spacing={{ xs: '0.75rem', sm: '0.5rem', md: '0.75rem' }}>
              <IconButton
                aria-label="previous"
                onClick={handlePrev}
                sx={{
                  bgcolor: (theme) => theme.palette.silver.main,
                  borderRadius: '50%',
                  width: '2.75rem',
                  height: '2.75rem',
                  '&:hover': { bgcolor: (theme) => theme.palette.lightGray.main },
                }}
              >
                <HXCIcon name="arrowLeftRg" color={'darkGray'} />
              </IconButton>
              <IconButton
                aria-label="next"
                onClick={handleNext}
                sx={{
                  bgcolor: (theme) => theme.palette.silver.main,
                  borderRadius: '50%',
                  width: '2.75rem',
                  height: '2.75rem',
                  '&:hover': { bgcolor: (theme) => theme.palette.lightGray.main },
                }}
              >
                <HXCIcon name="arrowRightRg" color={'darkGray'} />
              </IconButton>
            </Stack>
          )}
        </Stack>
        <Box
          className="pagination-box"
          aria-roledescription="carousel"
          sx={{
            position: 'relative',
            display: 'block',
          }}
        >
          <Box
            ref={emblaRef}
            sx={{
              overflow: 'hidden',
              pb: isDesktop ? '1rem' : '3rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: `${SLIDE_GAP_PX}px`,
              }}
            >
              {truncated.map((t, idx) => (
                <Box
                  key={`${t.name}-${idx}`}
                  aria-roledescription="slide"
                  sx={{
                    flex: `0 0 ${slideWidthPercent}`,
                    minWidth: 0,
                    overflow: 'visible',
                  }}
                >
                  <TestimonialCard {...t} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Mobile dot pagination */}
          {!isDesktop && (
            <CarouselDotPagination
              scrollSnaps={scrollSnaps}
              selectedIndex={selectedIndex}
              onDotClick={onDotClick}
              variant="black"
              sx={{ mt: '0.5rem' }}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default TestimonialCarousel;
